import c from "classnames";
import { ComponentProps, useState } from "react";

import { Icon } from "@design-system/Icon/Icon";
import { Spinner } from "@design-system/Spinner/Spinner";
import { Text } from "@design-system/Text/Text";

import classes from "./Button.module.css";

type Variant = "solid" | "outlined";
type Color = "primary" | "secondary" | "danger";
type Size = "sm" | "md" | "lg";

type Props = ComponentProps<"button"> & {
  variant?: Variant;
  color?: Color;
  stretch?: boolean;
  onClick?: () => Promise<void> | void;
  children?: string;
  uppercase?: boolean;
  icon?: ComponentProps<typeof Icon>["icon"];
  isLoading?: boolean;
  size?: Size;
};

export const Button = ({
  variant = "solid",
  color = "primary",
  stretch = false,
  type = "button",
  onClick,
  uppercase,
  children,
  icon,
  isLoading,
  size = "md",
  ...rest
}: Props) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const handleClick = async () => {
    try {
      const isAsync = onClick?.constructor.name === "AsyncFunction";

      if (isAsync) {
        setIsProcessing(true);
      }
      await onClick?.();
    } finally {
      setIsProcessing(false);
    }
  };

  const showSpinner = isLoading || isProcessing;

  return (
    <button
      className={c(
        classes.button,
        classes[variant],
        classes[color],
        stretch && classes.stretch,
        showSpinner && classes.loading,
        size && classes[size],
      )}
      disabled={rest.disabled || showSpinner}
      type={type}
      onClick={showSpinner ? undefined : handleClick}
      {...rest}
    >
      <div className={c(classes.content, showSpinner && classes.loading)}>
        {icon && <Icon size="sm" icon={icon} />}
        <Text
          size={uppercase ? 10 : 11}
          weight={500}
          color="inherit"
          spacing="relaxed"
          uppercase={uppercase}
        >
          {children}
        </Text>
      </div>
      {showSpinner && (
        <div className={classes.spinner}>
          <Spinner
            color={
              variant === "outlined"
                ? color === "danger"
                  ? "primary"
                  : color
                : "white"
            }
          />
        </div>
      )}
    </button>
  );
};
